import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import type { AvailableAddons, AvailableDiscounts, DiscountRequest, InsurerProductPartAvailable, Offer, OfferDiscounts, PackageItemModel, PagedQuery, PhotoDocumentation, WaitingDiscountRequests } from 'BrokerTsLib/src/dtos'
import type { GenericMutationOptions, GenericQueryOptions } from 'BrokerTsLib/src/hooks'
import { longStaleTime } from 'BrokerTsLib/src/hooks'

import type { GetOffersParams } from '@/context/OffersProvider'

import { Apps } from '@/dtos'
import { useUrl } from '.'
import { Apis } from '../appSettings/AppSettings'
import { usePartner } from '../context/PartnerContext'
import { useApp } from './useApp'

export type CalculateOffersRequest = {
  hubId: string | null
  offerIds?: number[]
  forceCalculation?: boolean
  paramsId?: string
}

export const useGetOffers = (paramsId: string, options?: GenericMutationOptions<Offer[]>) => {
  const { app } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasOffer : Apis.offer)

  return useMutation({ mutationFn: (variables?: GetOffersParams) => axios.get<Offer[]>(`${url}/GetOffers`, { params: { paramsId, ...variables } }), ...options })
}

export const useGetOffer = (offerId: number | undefined, options?: GenericQueryOptions<Offer>) => {
  const { app } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasOffer : Apis.offer)

  return useQuery({
    queryKey: ['useGetOffer', offerId],
    queryFn: () => axios.get<Offer>(`${url}/GetOffer?offerId=${offerId}`),
    enabled: offerId != null && (options?.enabled ?? true),
    ...options
  })
}

export const useGetOfferValid = (offerId: number) => {
  const { app } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasOffer : Apis.offer)


  return useQuery({
    queryKey: ['useGetOfferValid', offerId],
    queryFn: () => axios.get<boolean>(`${url}/GetInsurerOfferValid?insurerOfferId=${offerId}`).then(res => res.data)
  })
}

export const useCalculateOffers = () => {
  const queryClient = useQueryClient()

  const { app } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasOffer : Apis.offer)

  return useMutation({
    mutationFn: (data: CalculateOffersRequest) =>
      axios.post<Offer[]>(`${url}/CalculateOffers`, data),
    onSuccess(data) {
      for (const offer of data.data) {
        queryClient.invalidateQueries({ queryKey: ['useGetOfferCkp', offer.offerId] })
      }
    },
  })
}

export const useSelectCalculation = () => {
  const { app } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasOffer : Apis.offer)

  return useMutation({ mutationFn: (data: { paramsId: string, offerId: number }) =>
    axios.post(`${url}/SelectOffer`, data) })
}

export const useGetOfferAvailableDiscounts = (offerId: number, options?: GenericQueryOptions<AvailableDiscounts>) => {
  const url = useUrl(Apis.offer)

  return useQuery({
    queryKey: ['GetOfferAvailableDiscounts', offerId],
    queryFn: () => axios.get<AvailableDiscounts>(`${url}/GetOfferAvailableDiscounts?offerId=${offerId}`),
    staleTime: longStaleTime,
    ...options,
  })
}

export const useSaveOfferDiscounts = () => {
  const url = useUrl(Apis.offer)
  return useMutation({ mutationFn: ({ offerId, data }: { offerId: number, data: OfferDiscounts }) =>
    axios.post(`${url}/SaveOfferDiscounts?offerId=${offerId}`, data) })
}

export const useProcessDiscountRequest = (offerId: number) => {
  const url = useUrl(Apis.offer)
  return useMutation({ mutationFn: (data: DiscountRequest) =>
    axios.post(`${url}/ProcessDiscountRequest?offerId=${offerId}`, data) })
}

export const useWaitingDiscountRequests = () => {
  const url = useUrl(Apis.offer)
  return useMutation({ mutationFn: (data: PagedQuery) =>
    axios.post<WaitingDiscountRequests>(`${url}/GetWaitingDiscountRequest`, data) })
}

export const useGetOfferAvailableAddons = (offerId: number, options?: GenericQueryOptions<AvailableAddons[] | PackageItemModel[]>) => {
  const { app } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasOffer : Apis.offer)

  return useQuery({ queryKey: ['GetOfferAvailableAddons', offerId]
    , queryFn: () => axios.get<AvailableAddons[] | PackageItemModel[]>(`${url}/GetOfferAvailableAddons?offerId=${offerId}`)
    , staleTime: longStaleTime, ...options })
}

export const useSaveOfferAddons = (offerId: number) => {
  const { app } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasOffer : Apis.offer)

  return useMutation({ mutationFn: (data: InsurerProductPartAvailable[]) => axios.post(`${url}/SaveOfferAddons?offerId=${offerId}`, data) })
}

export const useSetOfferIncluded = () => {
  const { app } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasOffer : Apis.offer)

  return useMutation({ mutationFn: (data: { offerId: number, included: boolean }) => axios.post(`${url}/SetOfferIncluded`, data) })
}

export const useSetOfferStars = () => {
  const { app } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasOffer : Apis.offer)

  return useMutation({ mutationFn: (data: { offerId: number, star: number }) => axios.post(`${url}/SetOfferStars`, data) })
}

export const useSetOfferPaymentFrequency = () => {
  const { app } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasOffer : Apis.offer)
  return useMutation({ mutationFn: (data: { offerId: number, paymentFrequency: number }) => axios.post(`${url}/SetOfferPaymentFrequency`, data) })
}

export const useGetOfferCkp = (offerId: number, options?: GenericQueryOptions<{ ckpMonths: number, ckpClaims: number, message: string }>) => {
  const { app } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasOffer : Apis.offer)

  return useQuery({ queryKey: ['useGetOfferCkp', offerId]
    , queryFn: () => axios.get<{ ckpMonths: number, ckpClaims: number, message: string }>(`${url}/GetOfferCkp?offerId=${offerId}`)
    , staleTime: longStaleTime, ...options })
}

export const useClone = (offerId: number) => {
  const url = useUrl(Apis.offer)
  return useMutation({ mutationFn: () => axios.post<Offer>(`${url}/Clone?offerId=${offerId}`) })
}

export const useGetVehicleInspection = (offerId: number, options?: GenericQueryOptions<PhotoDocumentation[]>) => {
  const url = useUrl(Apis.offer)
  return useQuery({ queryKey: ['useGetVehicleInspection', offerId]
    , queryFn: () => axios.get<PhotoDocumentation[]>(`${url}/GetVehicleInspection?offerId=${offerId}`)
    , staleTime: longStaleTime, ...options })
}

export const useInvokeBmw = () => {
  const { getBaseUrl } = usePartner()

  return useMutation({ mutationFn: ({ offerId = '', action, paramsId }: {
    offerId?: number | '',
    action: 'Cancel' | 'Continue',
    paramsId: string
  }) =>
    axios.post<Offer>(`${getBaseUrl()}/InvokeBmw?offerId=${offerId}&action=${action}&paramsId=${paramsId}`)
  })
}

export const useConfirm = () => {
  const url = useUrl(Apis.offer)
  return useMutation({ mutationFn: (offerId: number) => axios.post<Offer>(`${url}/Confirm?offerId=${offerId}`) })
}

export const useGenerateMakeInsuranceOffers = () => {
  const url = useUrl(Apis.offer)

  return useMutation({ mutationFn: (paramsId: string) =>
    axios.post<number[]>(`${url}/GenerateMakeInsuranceOffers?paramsId=${paramsId}`) })
}

export const useDeleteInvalidOffers = () => {
  const url = useUrl(Apis.offer)

  return useMutation({ mutationFn: (paramsId: string) =>
    axios.post(`${url}/DeleteInvalidOffers?paramsId=${paramsId}`) })
}